import React, { useState } from 'react';

import { Paper, Stack } from '@mui/material';

import { isRowApproved, isRowManuallyApproved, isRowManuallyRequired } from '../../manualOverridePersistence';
import ChecklistRuleCard from '../../reportCards/ChecklistRuleCard';
import HiddenApprovedFooter from '../../reportCards/HiddenApprovedFooter';
import ReportCard from '../../reportCards/ReportCard';
import type { ManualOverrideState } from '../../types';
import PolicyBibleReferenceDialog from '../campaign/PolicyBibleReferenceDialog';

import type { TaxesPaymentsRowView } from './types';

interface Props {
  expandedRows: Record<string, boolean>;
  hiddenApprovedCount: number;
  manualOverrides: ManualOverrideState;
  rows: Array<TaxesPaymentsRowView>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onToggleDetails: (rowId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function TaxesPaymentsReportCard({
  expandedRows,
  hiddenApprovedCount,
  manualOverrides,
  rows,
  showAutoApproved,
  onApprovalChange,
  onToggleDetails,
  onUnapprovalChange,
}: Props) {
  const [policyBibleDialogRow, setPolicyBibleDialogRow] = useState<TaxesPaymentsRowView | null>(null);

  return (
    <ReportCard bordered={false}>
      <Stack spacing={1.5} sx={{ pb: 2 }}>
        {rows.length === 0 ? (
          <Paper variant="outlined" sx={{ color: 'text.secondary', p: 4, textAlign: 'center' }}>
            No Taxes &amp; Payments rows match this view.
          </Paper>
        ) : (
          rows.map((row) => {
            const manuallyApproved = isRowManuallyApproved(row, manualOverrides);
            const manuallyRequired = isRowManuallyRequired(row, manualOverrides);
            const approved = isRowApproved(row, manualOverrides);
            const expanded = !!expandedRows[row.id];
            const blocked = !approved && !manuallyRequired && row.status === 'blocked';

            return (
              <ChecklistRuleCard
                key={row.id}
                approved={approved}
                blocked={blocked}
                dataPoints={row.details}
                expanded={expanded}
                manuallyApproved={manuallyApproved}
                rule={row.rule}
                summary={row.summary}
                title={row.name}
                onApprovalChange={() => onApprovalChange(row.id)}
                onToggleExpanded={() => onToggleDetails(row.id)}
                onUnapprovalChange={() => onUnapprovalChange(row.id)}
                onViewPolicyBible={
                  row.field === 'Prize (Educational Package)' ? () => setPolicyBibleDialogRow(row) : undefined
                }
              />
            );
          })
        )}
      </Stack>
      <HiddenApprovedFooter
        count={hiddenApprovedCount}
        noun="Taxes & Payments rule"
        showAutoApproved={showAutoApproved}
      />
      <PolicyBibleReferenceDialog
        match={policyBibleDialogRow?.policyBibleMatch}
        open={!!policyBibleDialogRow}
        onClose={() => setPolicyBibleDialogRow(null)}
      />
    </ReportCard>
  );
}
